import {
  ButtonLooks,
  FontIcon,
  FontIcons,
  StandardButton,
  brandfolderColorPalette,
} from '@brandfolder/react';

import { t, Trans } from '@lingui/macro';
import React, {
  FormEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';

import { fetchJson } from '@api/ApiHelper';
import { DialogSizes, StandardDialog } from '@components/library/dialog';

import { TextSanitizationTooltip } from '@components/shared/sanitization';

import { Person } from '../../people_tags/customPeopleTagTypes';

import { PeopleNameTextfield } from './PeopleNameTextfield';

const MAX_NAME_LENGTH = 50;
const NAME_VALIDATION_REGEX = /[^a-zA-Z\s\-'\.]/g;

const PersonCardWrapper = styled.div`
  box-shadow: 1px 2px 6px 0 rgba(0, 0, 0, 0.09);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .default-image {
    background-color: ${brandfolderColorPalette.neutrals[200]};
    height: calc(100% - 52px);
  }
`;

const ImageWrapper = styled.div`
  cursor: pointer;
  display: flex;
  position: relative;
  width: 100%;
  height: 210px;
  overflow: hidden;

  &:hover .image-overlay {
    opacity: 1;
  }
`;

const LabelWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 52px;
  padding: 0 16px;

  .name-input-container {
    align-items: center;
    display: flex;
    width: 100%;

    .bf-textfield__inner {
      input {
      margin: 0 !important;
    }

  }

  .tag-person-textfield {
    width: 100%;

    input {
      height: 28px;
      min-height: 28px;
      padding: 8px 4px;
      width: 100%;
    }
  }
}
`;

const CardImage = styled.img`
  height: 210px;
  width: 100%;
  object-fit: cover;
`;

const DefaultImage = styled.div`
  align-items: center;
  color: ${brandfolderColorPalette.neutrals[500]};
  display: flex;
  height: 210px;
  justify-content: center;
  background-color: ${brandfolderColorPalette.neutrals[200]};
  width: 100%;
`;

const ImageOverlay = styled.div`
  align-items: center;
  background-color: rgba(0, 1, 1, 0.7);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: opacity 0.2s ease;
  width: 100%;
  z-index: 1;
`;

const OptionsButton = styled.div`
  color: ${brandfolderColorPalette.neutrals[0]};
  cursor: pointer;
  position: absolute;
  right: 5%;
  rotate: 90deg;
  top: 5%;
`;

const OptionModal = styled.div`
  background-color: ${brandfolderColorPalette.neutrals[0]};
  border-radius: 4px;
  padding: 8px;
  position: absolute;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px;

  &:hover {
    background-color: ${brandfolderColorPalette.neutrals[100]};
  }
`;

const IconContainer = styled.div`
  margin-right: 8px;
`;

const TextContainer = styled.div`
  margin-right: 10px;
  flex: 1;
`;

const InfoContainer = styled.div`
  margin-left: 8px;
  color: ${brandfolderColorPalette.primary[600]}
`;

const ClickableLabel = styled.div<{ $isPlaceholder?: boolean; $showPencil?: boolean; $showCross?: boolean }>`
  color: ${({ $isPlaceholder }): string =>
    $isPlaceholder
      ? brandfolderColorPalette.primary[600]
      : brandfolderColorPalette.neutrals[700]
};
  font-size: 14px;
  font-weight: 450;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  display: ${({ $isPlaceholder }): string => $isPlaceholder ? 'flex' : 'flex'};
  align-items: center;
  gap: ${({ $isPlaceholder }): string => $isPlaceholder ? '8px' : '8px'};
  position: relative;
  justify-content: space-between;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid ${brandfolderColorPalette.primary[600]};
    outline-offset: 2px;
  }

  .pencil-icon {
    opacity: ${({ $showPencil }): string => $showPencil ? '1' : '0'};
    transition: opacity 0.2s ease;
    margin-left: 8px;
  }
`;

const DialogContent = styled.div`
  margin-bottom: 24px;
`;

const DialogButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

// Custom dialog header styling
const StyledDialogHeader = styled.div`
  .dialog--heading {
    font-weight: 700 !important;
    font-size: 24px;
  }
`;

const EyeIcon = styled.div`
  position: absolute;
  color: ${brandfolderColorPalette.neutrals[0]};
  cursor: pointer;
  right: 5%;
  top: 5%;
  z-index: 2;
`;

const HiddenIdentityOverlay = styled.div`
  position: absolute;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%);
  width: 100%;
  height: 100%;
`;

export interface PersonCardProps {
  availablePeople?: Person[];
  imageUrl?: string;
  name?: string;
  personId?: string;
  onUpdateSuccess?: () => void;
  hidden?: boolean;
}

const useImageError = (imageUrl?: string): { hasImageError: boolean; handleImageError: () => void } => {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [imageUrl]);

  const handleImageError = useCallback(() => {
    setHasImageError(true);
  }, []);

  return { hasImageError, handleImageError };
};

const useNameInput = (
  initialName?: string,
  onShowNameField?: () => void
): {
    personName: string;
    showNameField: boolean;
    inputValue: string;
    formRef: React.RefObject<HTMLFormElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    setPersonLabel: (value: string) => void;
    validateAndSanitizeName: (value: string) => string;
    handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
    handleBlur: (e: React.FocusEvent) => void;
    handleShowNameField: () => void;
    handleKeyDown: (e: React.KeyboardEvent) => void;
    cancelEdit: () => void;
  } => {
  const [personName, setPersonName] = useState(initialName || '');
  const [showNameField, setShowNameField] = useState(false);
  const [inputValue, setInputValue] = useState(initialName || '');

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const newName = initialName || '';
    setPersonName(newName);
    setInputValue(newName);
  }, [initialName]);

  // Auto-focus input when field is shown
  useEffect(() => {
    if (showNameField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showNameField]);

  const validateAndSanitizeName = useCallback((inputName: string): string => {
    const sanitized = inputName.replace(NAME_VALIDATION_REGEX, '');
    return sanitized.slice(0, MAX_NAME_LENGTH);
  }, []);

  // Removed saveName - names are now only saved when selecting from dropdown

  const cancelEdit = useCallback(() => {
    setShowNameField(false);
    setInputValue(personName);
  }, [personName]);

  const setPersonLabel = useCallback((value: string) => {
    const sanitizedValue = validateAndSanitizeName(value);
    setInputValue(sanitizedValue);
  }, [validateAndSanitizeName]);

  // Removed updatePersonName - now handled in selection handlers
  const handleShowNameField = useCallback(() => {
    setShowNameField(true);
    onShowNameField?.();
  }, [onShowNameField]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleShowNameField();
    }
  }, [handleShowNameField]);

  return {
    personName,
    showNameField,
    inputValue,
    formRef,
    inputRef,
    setPersonLabel,
    validateAndSanitizeName,
    handleSubmit: (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      // Don't save on submit - only when selecting from dropdown
      cancelEdit();
    },
    handleBlur: (e: React.FocusEvent): void => {
      // Don't close if focus is moving within the form
      if (formRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }

      // Don't save on blur - only when selecting from dropdown
      cancelEdit();
    },
    handleShowNameField,
    handleKeyDown,
    cancelEdit,
  };
};

export const PersonCard: FunctionComponent<PersonCardProps> = ({
  // availablePeople = mockPeople,
  availablePeople = undefined,
  imageUrl = undefined,
  name = undefined,
  personId = undefined,
  onUpdateSuccess = undefined,
  hidden = false,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showOptionModal, setShowOptionModal] = useState<boolean>(false);
  const [hideIdentity, setHideIdentity] = useState<boolean>(false);
  const { hasImageError, handleImageError } = useImageError(imageUrl);
  const {
    personName,
    showNameField,
    inputValue,
    formRef,
    inputRef,
    setPersonLabel,
    validateAndSanitizeName,
    handleSubmit,
    handleBlur,
    handleShowNameField,
    handleKeyDown,
    cancelEdit,
  } = useNameInput(name, () => setIsHovering(false));

  const shouldShowImage = useMemo(() =>
    (imageUrl) && !hasImageError,
  [imageUrl, hasImageError]
  );

  useEffect(() => {
    if (showNameField) {
      setIsHovering(false);
    }
  }, [showNameField]);

  useEffect(() => {
    if (hidden) {
      setHideIdentity(true);
    }
  }, [hidden]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const brandfolderKey = BFG.brandfolder_key;
      const URL =
        `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/${personId}?name=${personName}`;
      await fetchJson({
        url: URL,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      setShowOptionModal(false);
      // Trigger refresh of people hub data after successful deletion
      onUpdateSuccess?.();
    } catch (error) {
      console.error('Failed to delete person tag:', error);
      setShowOptionModal(false);
    }
  }, [personId, onUpdateSuccess]);

  const handleDeleteCancel = useCallback(() => {
    setShowOptionModal(false);
  }, []);

  const nameDisplayProps = useMemo(() => ({
    'aria-label': t`Current name: ${personName}. Click to edit`,
    onClick: handleShowNameField,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
  }), [personName, handleShowNameField, handleKeyDown]);

  const placeholderProps = useMemo(() => ({
    'aria-label': t`Click to add a name for this person`,
    onClick: handleShowNameField,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
  }), [handleShowNameField, handleKeyDown]);

  function handleOptionClick(e: React.MouseEvent): void {
    e.stopPropagation();
    setShowOptions(true);
  }

  /* function handleDeleteIdentityClick(): void {
    setShowOptionModal(true);
    setShowOptions(false); // Close the options menu
  } */

  function handleOptionModalClose(): void {
    setShowOptionModal(false);
  }

  async function handleToggleIdentityVisibility(e: React.MouseEvent, shouldHide: boolean): Promise<void> {
    e.stopPropagation();
    try {
      setHideIdentity(shouldHide);
      if (shouldHide) {
        setShowOptions(false);
      }

      if (personId) {
        const brandfolderKey = BFG.brandfolder_key;
        const URL = `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/${personId}`;

        await fetchJson({
          url: URL,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: {
            metadata: {
              hide: shouldHide
            }
          },
        });
      }
    } catch (err) {
      console.error(`Error while ${shouldHide ? 'hiding' : 'unhiding'} identity`, err);
      // Revert the UI state if the API call failed
      setHideIdentity(!shouldHide);
    }
  }

  function handleIdentityImageClick(): void {
    if (BFG.brandfolder_slug && name) {
      const searchQuery = `people.strict:("${name}")`;
      const encodedQuery = encodeURIComponent(searchQuery);
      window.open(`/${BFG.brandfolder_slug}?q=${encodedQuery}`, '_blank');
    }
  }

  return (
    <PersonCardWrapper>
      <ImageWrapper className={hasImageError ? 'default-image' : undefined} onClick={handleIdentityImageClick}>
        {shouldShowImage ? (
          <CardImage
            alt={t`person`}
            onError={handleImageError}
            src={imageUrl}
          />
        ) : (
          <DefaultImage>
            <FontIcon
              icon={FontIcons.UserFilled}
              iconSize={68}
              title={t`default image`}
            />
          </DefaultImage>
        )}
        {/* Render eye icon outside of ImageOverlay when hideIdentity is true */}
        {hideIdentity && (
          <HiddenIdentityOverlay>
          <EyeIcon onClick={(e) => void handleToggleIdentityVisibility(e, false)}>
            <span className="icon bff-hide" title="Click to unhide identity"></span>
          </EyeIcon>
          </HiddenIdentityOverlay>
        )}
        <ImageOverlay className="image-overlay">
        {!hideIdentity && (
          <>
          <OptionsButton onClick={handleOptionClick}>
            <span className="bff-ellipsis"></span>
          </OptionsButton>
          {showOptions && (
            <OptionModal>
              <OptionItem onClick={(e) => void handleToggleIdentityVisibility(e, true)}>
                <IconContainer>
                  <span aria-hidden="false" aria-label={t`Mark as view only`} className="icon bff-hide"></span>
                </IconContainer>
                <TextContainer>
                  <p><Trans>Hide identity</Trans></p>
                </TextContainer>
                <InfoContainer>
                  <TextSanitizationTooltip
                    tooltipText={t`Hiding a person will hide the associated people tags from all assets
                      across the Brandfolder that are associated with this identity. This action is recoverable.`}
                  />
                </InfoContainer>
              </OptionItem>
              {/*<OptionItem onClick={handleDeleteIdentityClick}>
                <IconContainer>
                  <span aria-hidden="false" aria-label={t`Delete`} className="icon bff-trash"></span>
                </IconContainer>
                <TextContainer>
                  <p><Trans>Delete identity</Trans></p>
                </TextContainer>
                <InfoContainer>
                  <TextSanitizationTooltip
                      tooltipText={t`Deleting a person will permanently remove their identity from People Hub and all
                        associated people tags from every asset in this Brandfolder. This action is not recoverable.`}
                    />
                </InfoContainer>
              </OptionItem> */}
            </OptionModal>
          )}
          </>
        )}
        {/* Styled button */}
          <StandardButton
            look={ButtonLooks.Secondary}
            style={{
              color: brandfolderColorPalette.neutrals[0],
              backgroundColor: 'transparent',
              border: `1px solid ${brandfolderColorPalette.neutrals[0]}`
            }}
          >
            <Trans>View assets</Trans>
          </StandardButton>
        </ImageOverlay>
      </ImageWrapper>

      <LabelWrapper
        className="person-name-label-wrapper"
      >
        {personName && !showNameField && (
          <ClickableLabel
            {...nameDisplayProps}
            $showCross={isHovering}
            $showPencil={isHovering}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {personName}
            <FontIcon
              className="pencil-icon"
              icon={FontIcons.Edit}
              iconSize={14}
              style={{ color: brandfolderColorPalette.primary[600] }}
            />
          </ClickableLabel>
        )}

        {!personName && !showNameField && (
          <ClickableLabel $isPlaceholder {...placeholderProps}>
            <FontIcon icon={FontIcons.Tag} iconSize={16} />
            <Trans>Name person</Trans>
          </ClickableLabel>
        )}

        {showNameField && (
          <div className="name-input-container">
            <form
              ref={formRef}
              onBlur={handleBlur}
              onSubmit={handleSubmit}
            >
               <PeopleNameTextfield
                 availablePeople={availablePeople || []}
                 cancelEdit={() => cancelEdit()}
                 currentPersonId={personId}
                 currentPersonImage={imageUrl}
                 currentPersonName={personName}
                 onUpdateSuccess={onUpdateSuccess}
                 placeholder={t`Enter name...`}
                 searchTag={inputValue}
                 setPersonLabel={setPersonLabel}
                 textfieldRef={inputRef}
                 validateInput={validateAndSanitizeName}
               />
            </form>
          </div>
        )}
      </LabelWrapper>

      {/* Delete confirmation dialog - size prop added to limit dialog width and height */}
      <StyledDialogHeader>
        <StandardDialog
        height={300}
        id="delete-person-tag-confirmation"
        onClose={handleOptionModalClose}
        open={showOptionModal}
        setOpen={setShowOptionModal}
        showFooter={false}
        size={DialogSizes.Small}
        title={ personName ? `Permanently delete ${personName}'s identity?` : 'Permanently delete this identity?'}
        titleIcon={FontIcons.Trash}
        >
          <DialogContent>
            <Trans>
              Deleting this person will permanently remove their identity from People Hub and all associated people
              tags from every asset in this Brandfolder. This action is not recoverable, are you sure you want to
              continue?
            </Trans>
          </DialogContent>
          <DialogButtonContainer>
            <StandardButton
              look={ButtonLooks.Tertiary}
              onClick={handleDeleteCancel}
            >
              <Trans>Cancel</Trans>
            </StandardButton>
            <StandardButton
              look={ButtonLooks.Primary}
              onClick={() => void handleDeleteConfirm()}
            >
              <Trans>Delete identity</Trans>
            </StandardButton>
         </DialogButtonContainer>
         </StandardDialog>
      </StyledDialogHeader>
    </PersonCardWrapper>
  );
};
